import type { NovelItem } from '@app/_proto/Protos/novels';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import useChapterStatus, { type UseChapterStatusParam } from '@app/domains/common/hooks/useChapterStatus';
import useNovelStatus from '@app/domains/common/hooks/useNovelStatus';
import usePricingModelStatus from '@app/domains/common/hooks/usePricingModelStatus';
import useUserStatus from '@app/domains/common/hooks/useUserStatus';

export type UseChapterLockStatusParam = UseChapterStatusParam & {
    novel: NovelItem | undefined;
};

export type UseChapterLockStatusReturn = {
    isLockedBySneakPeekChapter?: boolean;
    isLockedByAdvancedChapter?: boolean;
    isLockedByWaitToUnlockChapter: boolean;
    isLockedByKarmaChapter?: boolean;
    isChapterLockedByFree?: boolean;
    isChapterLocked: boolean;
    isChapterLockedByVoucher: boolean;
};

export default function useChapterLockStatus({
    novel,
    chapter,
}: UseChapterLockStatusParam): UseChapterLockStatusReturn {
    const { isVipActive } = useUserStatus();
    const { isSneakPeekNovel } = useNovelStatus({ novel });
    const { isAdvancedChapter, isUnlockedChapter, isFreeChapter, isKarmaChapter } = useChapterStatus({
        chapter,
    });

    const series = chapter?.novelInfo?.series ?? novel?.series;
    const activePricingModels = series?.activePricingModels;

    const { activePricingModelType, freeInfo } = usePricingModelStatus({
        novel,
        chapter,
    });

    const { activeSubscription } = useChampionSubscriptions();

    const isLockedBySneakPeekChapter = !isVipActive && !activeSubscription && isSneakPeekNovel;
    const isLockedByAdvancedChapter = isAdvancedChapter && !isUnlockedChapter && !isFreeChapter;
    const isLockedByKarmaChapter =
        isKarmaChapter &&
        !isLockedBySneakPeekChapter &&
        !isLockedByAdvancedChapter &&
        !isUnlockedChapter &&
        !isFreeChapter;
    const isLockedByWaitToUnlockChapter =
        activePricingModelType === 'waitToUnlock' &&
        !isLockedBySneakPeekChapter &&
        !isLockedByAdvancedChapter &&
        !isUnlockedChapter &&
        !isFreeChapter;
    const isChapterLockedByFree =
        activePricingModelType === 'free' &&
        !isLockedBySneakPeekChapter &&
        !isLockedByAdvancedChapter &&
        !isUnlockedChapter &&
        !!freeInfo?.requiresLogin &&
        (chapter?.offset || 0) > (freeInfo.anonymousChaptersCount?.value || 0);
    const isChapterLockedByVoucher =
        (activePricingModels?.find(p => p.pricing.oneofKind === 'voucher') ?? false) &&
        !isLockedBySneakPeekChapter &&
        !isLockedByAdvancedChapter &&
        !isUnlockedChapter &&
        !isFreeChapter;

    const isChapterLocked =
        isLockedBySneakPeekChapter ||
        isLockedByAdvancedChapter ||
        isLockedByKarmaChapter ||
        isLockedByWaitToUnlockChapter ||
        isChapterLockedByFree;

    return {
        isLockedBySneakPeekChapter,
        isLockedByAdvancedChapter,
        isLockedByWaitToUnlockChapter,
        isLockedByKarmaChapter,
        isChapterLockedByFree,
        isChapterLocked,
        isChapterLockedByVoucher,
    };
}
